/**
 * ProArbitrageDiscoveryBanner — homepage discovery hook for the new Day 1-7 work.
 *
 * Renders ABOVE the opportunity feed so the new dual-engine Pro Arbitrage view
 * is discoverable without first clicking into an opportunity. Pulls the live
 * agreement-rate stat from /api/arbitrage/confidence-score for social proof.
 *
 * Dismissible — sets `pp_arb_banner_dismissed=1` in localStorage so power users
 * don't see it every session. Self-hides if no analyses are cached yet.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, X, ArrowRight, Play } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = "pp_arb_banner_dismissed";
// One-click demo opens this opp directly in the Pro Arbitrage tab.
// Default = opp 12 (has rich AEO Engine + AutoContent matches and GB-over-US disagreement).
const DEMO_OPP_FALLBACK = 12;

const ProArbitrageDiscoveryBanner = ({ onDemoOpen, onOpenPage }) => {
  const [confidence, setConfidence] = useState(null);
  const [availableOpps, setAvailableOpps] = useState([]);
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined" && localStorage.getItem(DISMISS_KEY) === "1"
  );

  useEffect(() => {
    if (dismissed) return undefined;
    let cancelled = false;
    Promise.all([
      axios.get(`${API_BASE}/arbitrage/confidence-score`, { timeout: 5000 }).catch(() => null),
      axios.get(`${API_BASE}/arbitrage/available-opps`, { timeout: 5000 }).catch(() => null),
    ]).then(([conf, avail]) => {
      if (cancelled) return;
      if (conf?.data) setConfidence(conf.data);
      if (avail?.data?.opportunity_ids) setAvailableOpps(avail.data.opportunity_ids);
    });
    return () => {
      cancelled = true;
    };
  }, [dismissed]);

  const onDismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  const handleSeeItLive = () => {
    // Take the user to the dedicated "Best Country to Launch" page where they
    // can browse every analyzed signal and open the full comparison.
    if (typeof onOpenPage === "function") {
      onOpenPage();
      return;
    }
    // Fallback (older mount points): open a single example directly.
    const demoOpp = availableOpps[0] || DEMO_OPP_FALLBACK;
    localStorage.setItem("pp_demo_open_arbitrage", String(demoOpp));
    if (typeof onDemoOpen === "function") {
      onDemoOpen(demoOpp);
    } else {
      window.dispatchEvent(new CustomEvent("pp:open-arbitrage-demo", { detail: { opp_id: demoOpp } }));
    }
  };

  // Self-hide if dismissed or no cached analyses to brag about
  if (dismissed) return null;
  if (!confidence || !confidence.total_opps || confidence.total_opps < 1) return null;

  const pct = Math.round((confidence.agreement_rate || 0) * 100);
  const disagreedCount = confidence.total_opps - confidence.agreed_count;

  return (
    <div
      data-testid="pro-arbitrage-discovery-banner"
      className="relative border-2 border-[#7C3AED] bg-gradient-to-r from-[#7C3AED]/5 via-white to-amber-50 p-4 mb-4 overflow-hidden"
    >
      {/* Dismiss */}
      <button
        type="button"
        onClick={onDismiss}
        data-testid="pro-arbitrage-banner-dismiss"
        aria-label="Dismiss"
        className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
      >
        <X size={14} strokeWidth={2.5} />
      </button>

      <div className="flex items-start gap-3 pr-8">
        <div className="w-10 h-10 bg-[#7C3AED] flex items-center justify-center shrink-0">
          <Sparkles size={20} className="text-white" strokeWidth={2.5} />
        </div>

        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-black uppercase tracking-wider bg-[#7C3AED] text-white px-2 py-0.5">
              New
            </span>
            <h3 className="text-base font-black tracking-tight text-zinc-900">
              See which country an opportunity fits best
            </h3>
          </div>

          <p className="text-sm text-zinc-700 leading-snug">
            Two independent methods &mdash; a number-crunching score and an AI &mdash; each pick
            the best country to launch each opportunity. Right now they{" "}
            <strong className="text-emerald-700">agree {pct}% of the time</strong>. When they
            disagree, we flag it &mdash; so you instantly know which picks are safe bets and which
            need your own judgment.
          </p>

          <p className="text-xs text-zinc-500 leading-snug">
            Want to see it? We'll open a real example for you &mdash; no signup, nothing to set up.
          </p>

          <div className="flex items-center gap-2 pt-2 flex-wrap">
            <button
              type="button"
              onClick={handleSeeItLive}
              data-testid="pro-arbitrage-see-live-btn"
              className="inline-flex items-center gap-1.5 bg-[#7C3AED] hover:bg-[#6929d4] text-white text-sm font-bold px-3 py-2 transition-colors"
            >
              <Play size={14} fill="currentColor" strokeWidth={0} />
              Open the page
              <ArrowRight size={14} />
            </button>
            <span className="text-[11px] text-zinc-500">
              browse every signal's best country
            </span>
          </div>

          <div className="flex items-center gap-2 pt-1 text-[11px] text-zinc-400 flex-wrap">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-zinc-100">
              <span className="w-1.5 h-1.5 bg-emerald-500" /> Compares US · UK · India
            </span>
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-zinc-100">
              Based on real revenue data
            </span>
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-zinc-100">
              No made-up numbers
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProArbitrageDiscoveryBanner;
